import {
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  ErrorHandler,
  APP_INITIALIZER,
  enableProdMode,
  importProvidersFrom
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router, RouterModule, TitleStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgIdleModule } from '@ng-idle/core';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import {
  QueryClient,
  provideAngularQuery
} from '@tanstack/angular-query-experimental';
import { AgGridModule } from 'ag-grid-angular';
import { MomentModule } from 'ngx-moment';
import { QuillModule } from 'ngx-quill';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { AppComponent } from './app/app.component';
import { routing } from './app/app.routing';
import {
  RollbarErrorHandler,
  RollbarService,
  rollBarFactory
} from './app/rollbar.service';
import { ApiClient, AuthenticationService } from './app/shared';
import { FormlyWrapperFieldsetComponent } from './app/shared/formly-wrapper-fieldset/formly-wrapper-fieldset.component';
import { CustomerNamePipe } from './app/shared/pipes/customer-name.pipe';
import { AuthGuardService } from './app/shared/services/auth-guard.service';
import { ConfigService } from './app/shared/services/config.service';
import { SearchService } from './app/shared/services/search.service';
import { WorkshopJobService } from './app/shared/services/workshop-job.service';
import { TemplatePageTitleStrategy } from './app/title-strategy.service';
import { environment } from './environments/environment';
import { CurrencyPipe } from '@angular/common';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { SentryErrorHandler } from './app/sentry-error-handler';
import * as Sentry from '@sentry/angular';
import { VERSION } from './environments/version';
if (environment.production) {
  enableProdMode();
}

export const DATE_FORMATS = {
  parse: {
    dateInput: 'DD-MM-YY'
  },
  display: {
    dateInput: 'DD-MM-YY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

Sentry.init({
  dsn: 'https://49581d52a1a5d535dec80c605f599f26@o4509401289588736.ingest.de.sentry.io/4509401291358288',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  release: VERSION.raw,
  environment: environment.name,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: true
    })
  ]
});

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      FlexLayoutModule,
      RouterModule,
      BrowserModule,
      FormsModule,
      ReactiveFormsModule,
      MomentModule,
      routing,
      FlexLayoutModule,
      ToastrModule.forRoot({
        timeOut: 1000 * 15,
        extendedTimeOut: 1000 * 15
      }),
      NgIdleModule.forRoot(),
      QuillModule.forRoot(),
      AgGridModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.production,
        // Register the ServiceWorker as soon as the application is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000'
      }),
      MatTableModule,
      FormlyModule.forRoot({
        wrappers: [
          { name: 'fieldset', component: FormlyWrapperFieldsetComponent }
        ]
      }),
      FormlyMaterialModule
    ),
    ApiClient,
    AuthenticationService,
    ToastrService,
    SearchService,
    ConfigService,
    AuthGuardService,
    CustomerNamePipe,
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true
    },
    { provide: TitleStrategy, useClass: TemplatePageTitleStrategy },
    WorkshopJobService,
    CurrencyPipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideAngularQuery(new QueryClient()),
    provideAnimations(),
    provideMomentDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS }
  ]
}).catch((err) => console.error(err));
